import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import apiClient from '../../api/axios';

// Story 22 — the default choice offered at employee deactivation for whether to also
// remove that employee's upcoming (future, unworked) shifts. Owner can still override
// per case at deactivation time; this only sets the confirm dialog's default.
function DeactivationShifts() {
  const { t } = useTranslation('deactivationSettings');
  const navigate = useNavigate();

  const [removeUpcoming, setRemoveUpcoming] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    setLoading(true);
    apiClient.get('/api/settings/deactivation-shifts')
      .then((res) => setRemoveUpcoming(!!res.data?.removeUpcomingShiftsOnDeactivation))
      .catch(() => setToast({ type: 'error', message: t('toast.loadFailed') }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put('/api/settings/deactivation-shifts', {
        removeUpcomingShiftsOnDeactivation: removeUpcoming,
      });
      setRemoveUpcoming(!!res.data?.removeUpcomingShiftsOnDeactivation);
      setToast({ type: 'success', message: t('toast.saved') });
    } catch (err) {
      const message = err.response?.data?.message || t('toast.saveFailed');
      setToast({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[60] flex items-center gap-2 rounded-lg border px-4 py-3 text-sm shadow-lg max-w-xs ${
            toast.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          <span className="flex-1">{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-current opacity-60 hover:opacity-100 focus:outline-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={[
          <button
            key="back"
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
          >
            <ChevronLeft size={15} />
            {t('back')}
          </button>,
        ]}
      />

      <div className="flex-1 px-6 py-6 max-w-xl mx-auto w-full">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="w-6 h-6 animate-spin text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
            </svg>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{t('card.title')}</h2>
              <p className="text-xs text-gray-500 mt-1">{t('card.description')}</p>
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={removeUpcoming}
                onChange={(e) => setRemoveUpcoming(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-300"
              />
              <span className="text-sm text-gray-700 leading-snug">{t('toggleLabel')}</span>
            </label>

            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 disabled:opacity-50 disabled:cursor-not-allowed transition hover:from-orange-600 hover:to-amber-700"
            >
              {t('buttons.save')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeactivationShifts;
